import React, { useCallback, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeRequest } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  buildResetToScheduledConfirmDetails,
  buildVoidAllApprovalsConfirmDetails,
  canVoidApprovals,
} from '../changeApprovalPlan';
import { canShowResetToScheduled } from '../changePermissions';
import ChangeApprovalsTab from './ChangeApprovalsTab';
import ChangeInfraGateDispatchPanel, {
  type InfraGateOpenButtonState,
} from './ChangeInfraGateDispatchPanel';

interface Props {
  change: ChangeRequest;
  canVoidApprovals?: boolean;
  onUpdated: () => Promise<void> | void;
}

const ChangeTransportTab: React.FC<Props> = ({ change, canVoidApprovals: canVoidProp, onUpdated }) => {
  const { user } = useAuth();
  const { showConfirm } = useModal();
  const [resetting, setResetting] = useState(false);
  const [voidingAll, setVoidingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gateOpenButton, setGateOpenButton] = useState<InfraGateOpenButtonState | null>(null);
  const showReset = canShowResetToScheduled(change, user);
  const canVoid =
    canVoidProp ?? canVoidApprovals(change, user, change.approval_settings, 'transport');

  const handleGateOpenState = useCallback((state: InfraGateOpenButtonState | null) => {
    setGateOpenButton(state);
  }, []);

  const handleResetToScheduled = async () => {
    const ok = await showConfirm({
      title: 'Resetar para Agendado',
      message:
        'Esta ação cancela as aprovações e as subtarefas desta mudança e volta o status para Agendado. Os chamados no Incidente não serão fechados.',
      details: buildResetToScheduledConfirmDetails(),
      variant: 'danger',
      confirmLabel: 'Resetar para Agendado',
      cancelLabel: 'Manter como está',
    });
    if (!ok) return;
    setResetting(true);
    setError(null);
    try {
      await changesApi.resetToScheduled(change.id);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível resetar a mudança para Agendado.'));
    } finally {
      setResetting(false);
    }
  };

  const handleVoidAllApprovals = async () => {
    const ok = await showConfirm({
      title: 'Cancelar todas as aprovações de área?',
      message:
        'Confira abaixo o que será anulado. Você pode voltar sem alterar nada ou confirmar para replanejar.',
      details: buildVoidAllApprovalsConfirmDetails(change.approvals),
      variant: 'danger',
      confirmLabel: 'Sim, cancelar aprovações',
      cancelLabel: 'Não cancelar',
    });
    if (!ok) return;
    setVoidingAll(true);
    setError(null);
    try {
      await changesApi.voidApprovals(change.id);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao cancelar as aprovações.'));
    } finally {
      setVoidingAll(false);
    }
  };

  const showToolbar =
    showReset || canVoid || Boolean(gateOpenButton?.visible);

  return (
    <div>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {showToolbar ? (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 className="mb-0">Transporte</h6>
          <div className="d-flex flex-wrap gap-2">
            {showReset ? (
              <Button
                size="sm"
                variant="outline-danger"
                disabled={resetting || voidingAll}
                onClick={() => void handleResetToScheduled()}
              >
                {resetting ? 'Resetando...' : 'Resetar para Agendado'}
              </Button>
            ) : null}
            {canVoid ? (
              <Button
                size="sm"
                variant="outline-danger"
                disabled={voidingAll || resetting}
                onClick={() => void handleVoidAllApprovals()}
              >
                {voidingAll ? 'Cancelando...' : 'Cancelar todas as aprovações'}
              </Button>
            ) : null}
            {gateOpenButton?.visible ? (
              <Button size="sm" variant="primary" onClick={() => gateOpenButton.open()}>
                Abrir subtarefa externa
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
      <ChangeInfraGateDispatchPanel
        change={change}
        onUpdated={onUpdated}
        delegateOpenButton
        onOpenButtonState={handleGateOpenState}
      />
      <ChangeApprovalsTab
        change={change}
        canVoidApprovals={canVoidProp}
        approvalScope="transport"
        suppressVoidAllButton
        onUpdated={onUpdated}
      />
    </div>
  );
};

export default ChangeTransportTab;
